<template>
  <section class="megafon-settings" data-testid="megafon-settings">
    <v-alert v-if="error" type="error" outlined dense>{{ error }}</v-alert>
    <v-progress-linear v-if="loading" indeterminate class="mb-3" />
    <template v-if="loaded">
      <v-alert :type="state.connected ? 'success' : 'info'" outlined dense>
        {{ state.connected ? $t('megafon.connected') : $t('megafon.not_connected') }}
        <span v-if="state.connected && state.last_synced_at" class="ml-2">{{ $t('megafon.last_synced', { date: formatDate(state.last_synced_at) }) }}</span>
      </v-alert>
      <v-alert v-if="hasSyncError" type="error" outlined dense data-testid="megafon-sync-error">
        {{ $t('megafon.sync_error') }}
      </v-alert>
      <p v-if="state.shared_branch_count > 1" class="caption">{{ $t('megafon.shared_branches', { count: state.shared_branch_count }) }}</p>
      <v-list v-if="state.branches && state.branches.length > 1" dense class="py-0 mb-2" data-testid="megafon-shared-branches">
        <v-subheader>{{ $t('megafon.shared_branch_list') }}</v-subheader>
        <v-list-item v-for="branch in state.branches" :key="branch.id || branch.company_id || branch.name">
          <v-list-item-content><v-list-item-title>{{ branch.name || branch.company_name || branch.id || branch.company_id }}</v-list-item-title></v-list-item-content>
        </v-list-item>
      </v-list>
      <v-alert v-if="state.connected && apiToken && state.shared_branch_count > 1" type="warning" outlined dense data-testid="megafon-token-rotation-warning">
        {{ $t('megafon.token_rotation_warning', { count: state.shared_branch_count }) }}
      </v-alert>
      <v-alert v-if="state.connected && !state.history_available" type="warning" outlined dense data-testid="megafon-history-unavailable">
        {{ $t('megafon.history_unavailable_settings') }}
      </v-alert>
      <v-row>
        <v-col cols="12" md="6">
          <IntegrationSecretField v-model="apiToken" :label="$t('megafon.api_token')" :is-set="state.api_token_set" :disabled="!canManage" testid="megafon-token" @clear="requestDisconnect" />
        </v-col>
        <v-col cols="12" md="6">
          <v-select v-model="settings.default_virtual_number_id" :items="state.numbers || []" item-text="number" item-value="id" :label="$t('megafon.default_number')" outlined clearable :disabled="!canManage" />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="settings.timezone" :label="$t('megafon.timezone')" outlined :disabled="!canEditTimezone" />
        </v-col>
        <v-col cols="12" md="6">
          <v-select v-model="settings.route_numbers" :items="state.numbers || []" item-text="number" item-value="number" :item-disabled="isNumberRoutedToOtherBranch" :label="$t('megafon.route_numbers')" :hint="$t('megafon.route_numbers_hint')" persistent-hint outlined multiple chips :disabled="!canManage" data-testid="megafon-route-numbers" />
        </v-col>
        <v-col cols="12" md="6">
          <v-select v-model="settings.callback_user_id" :items="state.users || []" item-text="name" item-value="id" :label="$t('megafon.callback_user')" outlined clearable :disabled="!canManage || !settings.auto_tasks_enabled" />
          <v-switch v-model="settings.auto_tasks_enabled" :label="$t('megafon.auto_tasks')" :disabled="!canEnableAutoTasks" />
          <p class="caption" data-testid="megafon-inbound-lead-info">{{ $t('megafon.inbound_lead_creation') }}</p>
        </v-col>
        <v-col cols="12" v-if="(state.employees || []).length">
          <h4>{{ $t('megafon.employee_mappings') }}</h4>
          <v-row v-for="user in state.users" :key="user.id">
            <v-col cols="6">{{ user.name }}</v-col>
            <v-col cols="6">
              <v-select :value="settings.employee_mappings[user.id]" :items="state.employees || []" item-text="name" item-value="id" :label="$t('megafon.megafon_employee')" outlined dense clearable :disabled="!canManage" :data-testid="`megafon-employee-${user.id}`" @input="setEmployeeMapping(user.id, $event)" />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <div class="d-flex flex-wrap align-center">
        <v-btn color="primary" :loading="saving" :disabled="!canManage || saving || (!state.connected && !apiToken)" @click="save">
          {{ state.connected ? $t('megafon.save_settings') : $t('megafon.connect') }}
        </v-btn>
        <v-btn v-if="state.connected" text color="error" class="ml-2" :disabled="saving || !canManage" @click="requestDisconnect">{{ $t('megafon.disconnect') }}</v-btn>
      </div>
      <MegafonIncomingSetup v-if="state.connected && canManage" :key="`${companyId}-${state.generation}`" :company-id="companyId" :can-manage="canManage" @changed="incomingChanged" />
    </template>
    <v-dialog v-model="confirmDisconnect" max-width="420">
      <v-card><v-card-title>{{ $t('megafon.disconnect_title') }}</v-card-title><v-card-text>{{ $t('megafon.disconnect_text') }}</v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="confirmDisconnect = false">{{ $t('megafon.cancel') }}</v-btn><v-btn color="error" text :loading="saving" @click="disconnect">{{ $t('megafon.disconnect') }}</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import IntegrationSecretField from './IntegrationSecretField.vue';
import MegafonIncomingSetup from './MegafonIncomingSetup.vue';

const emptyState = () => ({ connected: false, api_token_set: false, history_available: false, generation: null, shared_branch_count: 0, branches: [], employees: [], numbers: [], users: [], routes: [], settings: { default_virtual_number_id: null, employee_mappings: {}, callback_user_id: null, auto_tasks_enabled: false, route_numbers: [], timezone: 'Europe/Moscow' } });

export default {
  name: 'MegafonIntegration',
  components: { IntegrationSecretField, MegafonIncomingSetup },
  props: { companyId: { type: [Number, String], required: true }, canManage: { type: Boolean, default: false } },
  data() { return { state: emptyState(), settings: emptyState().settings, apiToken: '', loading: false, saving: false, loaded: false, error: '', confirmDisconnect: false }; },
  computed: {
    canEnableAutoTasks() { return this.canManage && !!this.state.history_available; },
    canEditTimezone() { return this.canManage && !this.state.connected; },
    hasSyncError() { return !!(this.state.connected && this.state.error_code); },
  },
  mounted() { this.load(); },
  methods: {
    incomingChanged({ generation }) { this.state = Object.assign({}, this.state, { generation }); },
    headers() { return { Authorization: this.$store.getters.getAuthToken }; },
    requestDisconnect() { if (this.canManage && !this.saving) this.confirmDisconnect = true; },
    async load() {
      this.loading = true; this.error = '';
      try {
        const { data } = await this.axios.get('/api/v1/integrations/megafon', { headers: this.headers() });
        this.state = Object.assign(emptyState(), data || {});
        this.settings = Object.assign({}, emptyState().settings, this.state.settings || {}, {
          employee_mappings: Object.assign({}, (this.state.settings || {}).employee_mappings || {}),
          route_numbers: ((this.state.settings || {}).route_numbers || []).slice(),
        });
        this.loaded = true;
      } catch (e) { this.error = this.errorMessage(e); }
      finally { this.loading = false; }
    },
    async save() {
      if (!this.canManage || this.saving) return;
      if (this.settings.auto_tasks_enabled && !this.settings.callback_user_id) {
        this.error = this.$t('megafon.callback_required');
        return;
      }
      if (this.settings.auto_tasks_enabled && !(this.settings.route_numbers || []).length) {
        this.error = this.$t('megafon.route_numbers_required', { field: this.$t('megafon.route_numbers') });
        return;
      }
      this.saving = true; this.error = '';
      const payloadSettings = this.serializeSettings();
      try {
        if (!this.state.connected || this.apiToken) {
          const response = await this.axios.post('/api/v1/integrations/megafon', { api_token: this.apiToken, expected_company_id: this.companyId, generation: this.state.generation, timezone: payloadSettings.timezone }, { headers: this.headers() });
          this.apiToken = '';
          if (response && response.data) this.state = Object.assign({}, this.state, response.data);
          await this.load();
          if (!this.state.connected || this.state.generation == null) {
            this.settings = payloadSettings;
            if (!this.error) this.error = this.$t('megafon.request_error');
            return;
          }
          if (this.state.shared_branch_count > 1 && this.settings.timezone) {
            // A sibling binding joins the existing account's timezone; the API owns it.
            payloadSettings.timezone = this.settings.timezone;
          }
          // Connecting/rotating the token returns a fresh generation. Apply all staged branch
          // settings with that generation so the first save cannot silently discard them.
          this.settings = payloadSettings;
        }
        await this.axios.patch('/api/v1/integrations/megafon', { generation: this.state.generation, expected_company_id: this.companyId, settings: payloadSettings }, { headers: this.headers() });
        await this.load();
      } catch (e) {
        this.settings = payloadSettings;
        this.error = this.errorMessage(e);
      }
      finally { this.saving = false; }
    },
    async disconnect() {
      if (!this.canManage || this.saving) return;
      this.saving = true; this.error = '';
      try {
        await this.axios.delete('/api/v1/integrations/megafon', { headers: this.headers(), params: { generation: this.state.generation, expected_company_id: this.companyId } });
        this.confirmDisconnect = false; await this.load();
      } catch (e) { this.error = this.errorMessage(e); }
      finally { this.saving = false; }
    },
    serializeSettings() {
      return Object.assign({}, this.settings, {
        route_numbers: (this.settings.route_numbers || []).slice(),
        employee_mappings: Object.assign({}, this.settings.employee_mappings || {}),
      });
    },
    setEmployeeMapping(userId, employeeId) {
      this.$set(this.settings.employee_mappings, userId, employeeId);
    },
    isNumberRoutedToOtherBranch(number) {
      const routeItem = typeof number === 'object' ? number : null;
      const routeNumber = routeItem ? routeItem.number : number;
      const route = (this.state.routes || []).find((item) => {
        if (typeof item === 'string') return item === routeNumber;
        const resolvedNumber = item.number || ((this.state.numbers || []).find((entry) => String(entry.id) === String(item.virtual_number_id)) || {}).number;
        return String(resolvedNumber) === String(routeNumber);
      });
      return !!(route && route.company_id && String(route.company_id) !== String(this.companyId));
    },
    errorMessage(e) {
      const error = e && e.response && e.response.data && e.response.data.error;
      if (error && error.code === 'callback_setup_required') {
        return this.settings && (this.settings.route_numbers || []).length
          ? this.$t('megafon.callback_required')
          : this.$t('megafon.route_numbers_required', { field: this.$t('megafon.route_numbers') });
      }
      return (error && error.message) || this.$t('megafon.request_error');
    },
    formatDate(value) { return value ? new Date(value).toLocaleString() : ''; },
  },
};
</script>
